<script setup lang="ts">
import { useState } from '#app'
import { computed } from 'vue'
import { useCustomPageElementsActions } from '~/layers/cms/app/composables/useCustomPageElementsActions'
import { useAuthStore } from '../../stores/authStore'
import type { CmsComponent, CmsData } from '../../types/cmsTypes'
import { chooseElementByValuesPath } from '../../plugins/cms-components-list-block'
import { getByPath } from '../../utils/cmsDataHelper'

const props = defineProps<{
  cmsDataPath: (string | number)[]
  global?: boolean
}>()

const { allComponents, remove_element, move_element_down, move_element_up, duplicate_element } = useCustomPageElementsActions()

const cmsData = useState<CmsData>('cmsData')

const CustomPageCmsComponentsList = computed(() => {
  const list = getByPath(cmsData.value, props.cmsDataPath) ?? []
  if (!list.component_data) list.component_data = []
  return (Array.from(list.component_data as CmsComponent[]))
    .filter(item => item.component_type in allComponents)
    .map(item => ({
      component: allComponents[item.component_type as keyof typeof allComponents],
      data: item
    }))
})

const duplicate = (index: number) => {
  chooseElementByValuesPath(props.cmsDataPath)
  duplicate_element(index)
}
const remove = (index: number) => {
  chooseElementByValuesPath(props.cmsDataPath)
  remove_element(index)
}
const move_up = (index: number) => {
  chooseElementByValuesPath(props.cmsDataPath)
  move_element_up(index)
}
const move_down = (index: number) => {
  chooseElementByValuesPath(props.cmsDataPath)
  move_element_down(index)
}

const auth = useAuthStore()
</script>

<template>
  <div
    v-cms-component-list-block="{ path: props.cmsDataPath, global: props.global }"
    class="min-h-5 min-w-5"
  >
    <div v-if="auth.authorized">
      <client-only>
        <div
          v-for="(item, index) in CustomPageCmsComponentsList"
          :key="`${JSON.stringify(item)}-${index}`"
        >
          <div
            class="grid grid-rows-1 grid-cols-1 group/editContainer relative hover:outline-green-600"
            :class="'outline-1 outline-black'"
          >
            <div
              class="row-start-1 row-end-1 col-start-1 col-end-1 pointer-events-none absolute top-0 right-0 z-20 opacity-0 group-hover/editContainer:opacity-100"
            >
              <div class="flex flex-row self-end">
                <div
                  class="pointer-events-none group-hover/editContainer:pointer-events-auto h-10 w-10 p-1 bg-black/80 cursor-pointer flex items-center justify-center group/edit"
                  @click="duplicate(index)"
                >
                  <i
                    class="pi pi-copy size-5 group-hover/edit:size-6 duration-200 transition-all text-white"
                  />
                </div>
                <div
                  class="pointer-events-none group-hover/editContainer:pointer-events-auto h-10 w-10 p-1 bg-black/80 cursor-pointer flex items-center justify-center group/edit"
                  @click="remove(index)"
                >
                  <i
                    class="pi pi-times size-5 group-hover/edit:size-6 duration-200 transition-all text-white"
                  />
                </div>
                <div
                  v-if="index != 0"
                  class="pointer-events-none group-hover/editContainer:pointer-events-auto h-10 w-10 p-1 bg-black/80 cursor-pointer flex items-center justify-center group/edit"
                  @click="move_up(index)"
                >
                  <i
                    class="pi pi-arrow-up size-5 group-hover/edit:size-6 duration-200 transition-all text-white"
                  />
                </div>
                <div
                  v-if="index != CustomPageCmsComponentsList.length -1"
                  class="pointer-events-none group-hover/editContainer:pointer-events-auto h-10 w-10 p-1 bg-black/80 cursor-pointer flex items-center justify-center group/edit"
                  @click="move_down(index)"
                >
                  <i
                    class="pi pi-arrow-down size-5 group-hover/edit:size-6 duration-200 transition-all text-white"
                  />
                </div>
              </div>
            </div>

            <div
              class="row-start-1 row-end-1 col-start-1 col-end-1"
            >
              <component
                :is="item.component"
                :cms-data-path="[...props.cmsDataPath, 'component_data', index]"
              />
            </div>
          </div>
        </div>
      </client-only>
    </div>
    <div v-else>
      <div
        v-for="(item, index_na) in CustomPageCmsComponentsList"
        :key="`${JSON.stringify(item)}-${index_na}`"
      >
        <component
          :is="item.component"
          :cms-data-path="[...props.cmsDataPath, 'component_data', index_na]"
        />
      </div>
    </div>
  </div>
</template>
